import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import { getFilesByMuestraId } from "../../api/Muestras/muestraApi";
import { getFileContent } from "../../api/Design/fileApi";
import { setCachedArchivos } from "../../utils/cacheArchivos";
import {
  pickFrontAndBackFromExport,
  isMuestraImageFile,
} from "../../utils/muestraFotosExport";
import {
  getMuestraFileCategory,
  isTechnicalMuestraFile,
} from "../../utils/muestraFileCategories";
import { renderPdfPagesFromBase64 } from "../../utils/pdfThumbnail";
import { sortMuestrasForExport } from "./muestrasExcelExport";
import { pickFirstTechnicalSheetPage } from "./muestraPdfPageSelection";

const IMAGE_FETCH_CONCURRENCY = 4;

/** Preferencia al elegir ficha técnica para página completa */
const TECH_SHEET_PRIORITY = ["Ficha Técnica", "Ficha Diseño", "3D", "Patrones"];

function isPdfFile(file) {
  const mime = String(file?.mimeType || "").toLowerCase();
  if (mime.includes("pdf")) return true;
  const name = String(file?.name || file?.path || "").toLowerCase();
  return name.endsWith(".pdf");
}

function isTechnicalSheetCandidate(file) {
  if (!file || !isTechnicalMuestraFile(file)) return false;
  return isMuestraImageFile(file) || isPdfFile(file);
}

/**
 * Primera ficha técnica usable (imagen o PDF) a nivel muestra.
 * Prioriza Ficha Técnica → Diseño → 3D → Patrones.
 */
export function pickFirstTechnicalSheet(files = []) {
  const techFiles = (files || []).filter(isTechnicalSheetCandidate);
  if (!techFiles.length) return null;

  for (const folder of TECH_SHEET_PRIORITY) {
    const match = techFiles.find(
      (file) => getMuestraFileCategory(file) === folder,
    );
    if (match) return match;
  }

  // Legacy a nivel diseño: category null pero path técnico
  return techFiles[0];
}

/** @deprecated use pickFirstTechnicalSheet */
export function pickFirstTechnicalSheetImage(files = []) {
  return pickFirstTechnicalSheet(files);
}

/** A3 landscape (mismo MediaBox que PPT_BASE_SDS.pdf) */
const PAGE = { width: 1190.55, height: 841.89 };

const FOOTER_CONTACT = "Irène Guijt | irene@stagedesign.es | +34 667 765 007";

const FOOTER_H = 66;
const MARGIN_X = 36;
const MARGIN_Y = 32;
/** Baseline de STAGE: deja margen para ascendentes del logo y del código (44pt). */
const HEADER_LOGO_BASELINE = MARGIN_Y + 40;
const PHOTO_GAP = 10;

async function mapWithConcurrency(items, concurrency, fn) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex;
      nextIndex += 1;
      results[i] = await fn(items[i], i);
    }
  }

  const workers = Math.min(concurrency, items.length || 1);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Lee orientación EXIF (1–8). 1 = normal. */
function readJpegExifOrientation(bytes) {
  if (!bytes || bytes.length < 4) return 1;
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return 1;

  let offset = 2;
  while (offset + 4 < bytes.length) {
    if (bytes[offset] !== 0xff) break;
    const marker = bytes[offset + 1];
    const size = (bytes[offset + 2] << 8) | bytes[offset + 3];
    if (marker === 0xe1) {
      const start = offset + 4;
      if (
        bytes[start] === 0x45 &&
        bytes[start + 1] === 0x78 &&
        bytes[start + 2] === 0x69 &&
        bytes[start + 3] === 0x66
      ) {
        const tiff = start + 6;
        const little = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
        const read16 = (o) =>
          little
            ? bytes[o] | (bytes[o + 1] << 8)
            : (bytes[o] << 8) | bytes[o + 1];
        const read32 = (o) =>
          little
            ? bytes[o] |
              (bytes[o + 1] << 8) |
              (bytes[o + 2] << 16) |
              (bytes[o + 3] << 24)
            : (bytes[o] << 24) |
              (bytes[o + 1] << 16) |
              (bytes[o + 2] << 8) |
              bytes[o + 3];

        const ifd0 = tiff + read32(tiff + 4);
        const entries = read16(ifd0);
        for (let i = 0; i < entries; i += 1) {
          const entry = ifd0 + 2 + i * 12;
          if (read16(entry) === 0x0112) {
            const orientation = read16(entry + 8);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }
      }
      break;
    }
    if (marker === 0xda) break; // SOS
    offset += 2 + size;
  }
  return 1;
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Normaliza la foto a orientación vertical correcta (aplica EXIF)
 * y devuelve JPEG listo para jsPDF (que ignora EXIF).
 */
async function normalizeImageForPdf(base64, mimeType) {
  const dataUrl = `data:${mimeType};base64,${base64}`;
  const bytes = base64ToUint8Array(base64);
  const orientation =
    mimeType?.includes("jpeg") || mimeType?.includes("jpg")
      ? readJpegExifOrientation(bytes)
      : 1;

  // createImageBitmap aplica EXIF si está disponible
  try {
    const blob = new Blob([bytes], { type: mimeType || "image/jpeg" });
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(blob, {
        imageOrientation: "from-image",
      });
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close?.();
      const out = canvas.toDataURL("image/jpeg", 0.92);
      return {
        dataUrl: out,
        format: "JPEG",
        mimeType: "image/jpeg",
        width: canvas.width,
        height: canvas.height,
      };
    }
  } catch (err) {
    console.warn("createImageBitmap falló, uso canvas+EXIF", err);
  }

  const img = await loadHtmlImage(dataUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const swap = orientation >= 5 && orientation <= 8;
  canvas.width = swap ? ih : iw;
  canvas.height = swap ? iw : ih;

  switch (orientation) {
    case 2:
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      break;
    case 3:
      ctx.translate(canvas.width, canvas.height);
      ctx.rotate(Math.PI);
      break;
    case 4:
      ctx.translate(0, canvas.height);
      ctx.scale(1, -1);
      break;
    case 5:
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    case 6:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(0, -ih);
      break;
    case 7:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(canvas.width, -ih);
      ctx.scale(-1, 1);
      break;
    case 8:
      ctx.rotate(-0.5 * Math.PI);
      ctx.translate(-iw, 0);
      break;
    default:
      break;
  }

  ctx.drawImage(img, 0, 0);
  const out = canvas.toDataURL("image/jpeg", 0.92);
  return {
    dataUrl: out,
    format: "JPEG",
    mimeType: "image/jpeg",
    width: canvas.width,
    height: canvas.height,
  };
}

async function loadImageData(file) {
  if (!file?.path) return null;
  try {
    const response = await getFileContent(file.path);
    const { mimeType, data } = response?.data || {};
    if (!data || !mimeType?.includes("image")) return null;
    return await normalizeImageForPdf(data, mimeType);
  } catch (err) {
    console.warn("No se pudo cargar imagen PDF", file.path, err);
    return null;
  }
}

/** Carga únicamente la primera página de la ficha técnica. */
async function loadTechnicalSheetPages(file) {
  if (!file?.path) return [];
  try {
    const response = await getFileContent(file.path);
    const { mimeType, data } = response?.data || {};
    if (!data) return [];

    const mime = String(mimeType || "").toLowerCase();
    const looksPdf = mime.includes("pdf") || isPdfFile(file);

    if (looksPdf) {
      return await renderPdfPagesFromBase64(data, 2200, 1);
    }

    if (mime.includes("image") || isMuestraImageFile(file)) {
      const img = await normalizeImageForPdf(data, mimeType || "image/jpeg");
      return img ? [img] : [];
    }

    return [];
  } catch (err) {
    console.warn("No se pudo cargar ficha técnica para PDF", file.path, err);
    return [];
  }
}

async function fetchFrontBackImages(muestraId, fotosExport) {
  try {
    const data = await getFilesByMuestraId(muestraId);
    const files = Array.isArray(data) ? data : [];
    setCachedArchivos(muestraId, files);
    const { front, back } = pickFrontAndBackFromExport(files, fotosExport);
    const [frontImg, backImg] = await Promise.all([
      loadImageData(front),
      loadImageData(back),
    ]);

    // Sin fotos de muestra → primera página de la ficha técnica a página completa
    if (!frontImg && !backImg) {
      const techFile = pickFirstTechnicalSheet(files);
      const pages = techFile ? await loadTechnicalSheetPages(techFile) : [];
      return {
        front: null,
        back: null,
        fullPage: pickFirstTechnicalSheetPage(pages),
      };
    }

    return { front: frontImg, back: backImg, fullPage: null };
  } catch (err) {
    console.warn("Error listando fotos para PDF", muestraId, err);
    return { front: null, back: null, fullPage: null };
  }
}

function styleNameFor(m) {
  return (m.NOMBRE_DISENO || m.NombreCliente || m.MUESTRA || "—")
    .toString()
    .toUpperCase();
}

function fabricColorFor(m) {
  const tejido = [m.CALIDAD_NOMBRE, m.TEJIDO, m.FABRIC]
    .map((v) => (v == null ? "" : String(v).trim()))
    .find((v) => v && v !== "Desconocido" && v !== "Desconocida");
  const color = [m.COLOR_PANTONE, m.COLOR]
    .map((v) => (v == null ? "" : String(v).trim()))
    .find((v) => v && v !== "Desconocido" && v !== "Desconocida");
  return [tejido, color].filter(Boolean).join(" · ") || "—";
}

/**
 * Imagen centrada en el slot, sin marco (fondo blanco de página).
 */
function drawImageInSlot(doc, image, x, y, w, h) {
  if (!image?.dataUrl) {
    doc.setTextColor(170);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("Sin foto", x + w / 2, y + h / 2, { align: "center" });
    doc.setTextColor(0);
    return;
  }

  let drawW = w;
  let drawH = h;
  let drawX = x;
  let drawY = y;

  if (image.width > 0 && image.height > 0) {
    const scale = Math.min(w / image.width, h / image.height);
    drawW = image.width * scale;
    drawH = image.height * scale;
    drawX = x + (w - drawW) / 2;
    drawY = y + (h - drawH) / 2;
  }

  try {
    doc.addImage(
      image.dataUrl,
      image.format,
      drawX,
      drawY,
      drawW,
      drawH,
      undefined,
      "FAST",
    );
  } catch (err) {
    try {
      doc.addImage(
        image.dataUrl,
        "JPEG",
        drawX,
        drawY,
        drawW,
        drawH,
        undefined,
        "FAST",
      );
    } catch (err2) {
      console.warn("No se pudo incrustar imagen en PDF", err2);
    }
  }
}

/** Línea superior con topes; hueco opcional para el bloque del logo. */
function drawHeaderTopRule(doc, left, right, y, gapLeft, gapRight) {
  const tick = 7;
  doc.setDrawColor(35);
  doc.setLineWidth(0.65);
  if (gapLeft != null && gapRight != null && gapLeft < gapRight) {
    doc.line(left, y, gapLeft, y);
    doc.line(gapRight, y, right, y);
  } else {
    doc.line(left, y, right, y);
  }
  doc.line(left, y - tick, left, y + tick);
  doc.line(right, y - tick, right, y + tick);
}

/**
 * Logo STAGE / DESIGN / STUDIO.
 * Devuelve el bloque del logo y la Y de la raya de DESIGN (para la regla de cabecera).
 */
function drawStageWordmark(doc, x, y) {
  const gray = 155;
  const size = 26;
  const lineH = 30;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(size);

  const design = "DESIGN";
  const stageW = doc.getTextWidth("STAGE");
  const designW = doc.getTextWidth(design);
  const studioW = doc.getTextWidth("STUDIO");
  const logoW = Math.max(stageW, designW, studioW);
  const whisker = 22;
  const padX = whisker + 6;
  const padTop = size;
  const padBottom = 10;
  const blockH = lineH * 2 + padBottom;

  // Placa blanca: tapa cualquier raya de página debajo del logo
  doc.setFillColor(255, 255, 255);
  doc.rect(x - padX, y - padTop, logoW + padX * 2, blockH + padTop, "F");

  doc.setTextColor(gray, gray, gray);
  doc.text("STAGE", x, y);

  const designY = y + lineH;
  const designLineY = designY - 8;
  doc.text(design, x, designY);

  doc.text("STUDIO", x, y + lineH * 2);
  doc.setTextColor(0);

  return {
    left: x - padX,
    right: x + logoW + padX,
    bottom: y - padTop + blockH + padTop,
    designLineY,
  };
}

function drawFooterBand(doc, left, right, yCenter) {
  const midX = (left + right) / 2;
  doc.setDrawColor(60);
  doc.setLineWidth(0.7);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(40);
  const title = "Stage Design Studio";
  const titleW = doc.getTextWidth(title);
  const gap = 14;
  doc.line(left, yCenter, midX - titleW / 2 - gap, yCenter);
  doc.line(midX + titleW / 2 + gap, yCenter, right, yCenter);
  doc.text(title, midX, yCenter + 4, { align: "center" });

  const contactY = yCenter + 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100);
  const contactW = doc.getTextWidth(FOOTER_CONTACT);
  doc.setDrawColor(140);
  doc.setLineWidth(0.5);
  doc.line(left, contactY, midX - contactW / 2 - gap, contactY);
  doc.line(midX + contactW / 2 + gap, contactY, right, contactY);
  doc.text(FOOTER_CONTACT, midX, contactY + 3, { align: "center" });
  doc.setTextColor(0);
}

function drawSdsPage(doc, muestra, images) {
  const { width: W, height: H } = PAGE;
  const left = MARGIN_X;
  const right = W - MARGIN_X;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, H, "F");

  // ─── HEADER ───
  const logoBaseline = HEADER_LOGO_BASELINE;
  const logoBox = drawStageWordmark(doc, left, logoBaseline);
  drawHeaderTopRule(
    doc,
    left,
    right,
    logoBox.designLineY,
    logoBox.left,
    logoBox.right,
  );

  const style = styleNameFor(muestra);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(44);
  doc.setTextColor(168, 168, 168);
  const styleMaxW = 640;
  const styleLines = doc.splitTextToSize(style, styleMaxW);
  const styleX = right;
  const styleY = logoBaseline + 2;
  styleLines.slice(0, 2).forEach((line, i) => {
    doc.text(line, styleX, styleY + i * 46, { align: "right" });
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30);
  doc.text(
    fabricColorFor(muestra),
    styleX,
    styleY + Math.min(styleLines.length, 2) * 46 + 8,
    { align: "right" },
  );
  doc.setTextColor(0);

  // ─── FOOTER ───
  const footerTop = H - MARGIN_Y - FOOTER_H;
  drawFooterBand(doc, left, right, footerTop + 18);

  // ─── PHOTOS: máximo espacio útil, sin marco ───
  const headerBottom = Math.max(
    logoBox.bottom,
    styleY + Math.min(styleLines.length, 2) * 46 + 16,
  );
  const photosTop = headerBottom + 4;
  const photosBottom = footerTop - 4;
  const photosH = Math.max(160, photosBottom - photosTop);
  const photosW = right - left;

  // Sin fotos pero con ficha técnica → una sola imagen a todo el ancho
  if (images?.fullPage && !images?.front && !images?.back) {
    drawImageInSlot(doc, images.fullPage, left, photosTop, photosW, photosH);
    return;
  }

  const slotW = (photosW - PHOTO_GAP) / 2;

  drawImageInSlot(doc, images?.front, left, photosTop, slotW, photosH);
  drawImageInSlot(
    doc,
    images?.back,
    left + slotW + PHOTO_GAP,
    photosTop,
    slotW,
    photosH,
  );
}

/**
 * Exporta muestras a PDF SDS (1 página / muestra).
 * Cabecera logo+título, fotos front/back alineadas en el medio, pie Stage.
 */
export async function exportMuestrasToPdf({
  muestras,
  selectedIds,
  filename = "Muestras_SDS.pdf",
  onProgress,
}) {
  const idSet = new Set(
    (selectedIds || []).map((id) => Number(id)).filter(Number.isFinite),
  );

  const selected = (muestras || [])
    .filter((m) => idSet.has(Number(m.id ?? m.ID_MUESTRA)))
    .sort(sortMuestrasForExport);

  if (!selected.length) {
    throw new Error("No hay muestras seleccionadas para exportar");
  }

  const total = selected.length;
  onProgress?.({ current: 0, total });

  const imagesByIndex = await mapWithConcurrency(
    selected,
    IMAGE_FETCH_CONCURRENCY,
    async (m, index) => {
      const muestraId = m.id ?? m.ID_MUESTRA;
      const imgs = await fetchFrontBackImages(muestraId, m.FOTOS_EXPORT);
      onProgress?.({ current: index + 1, total });
      return imgs;
    },
  );

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: [PAGE.width, PAGE.height],
    compress: true,
  });

  selected.forEach((m, index) => {
    const imgs = imagesByIndex[index] || {};

    if (index > 0) doc.addPage([PAGE.width, PAGE.height], "landscape");
    drawSdsPage(doc, m, imgs);
  });

  const blob = doc.output("blob");
  saveAs(blob, filename);
}
